import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from "@stencil/core";
import { GalleryLoaderService } from "../../services/gallery-loader-service";
import { ResizeObserver } from "@juggle/resize-observer";
import { sendToGoogleAnalytics, findSharedItemPopup, AppStoreType, AppStateType } from "../../utils/utils";
import { widgetIdUpdated, widgetGalleryIdUpdated, widgetShopIdUpdated } from "../../store/widgetId";
import { setEnvironment } from "../../store/url";
import { widgetWidthUpdated, windowHeightUpdated } from "../../store/size";
import { setPreviewHoverMode, setPreviewMode, setPreviewPopupMode } from "../../store/preview";
import { setAnalytics } from "../../store/analytics";
import configureStore from "../../store/configureStore";
import { getWidgetId, sendBeacon } from "../../store/storeUtils";
import { getUseAnalytics } from "../../store/getters";
import { setSettings } from "../../store/settings";
import { Unsubscribe } from "../../utils/types";

@Component({
  tag: "covet-pics-widget",
  styleUrl: "covet-pics-widget.scss",
  shadow: true
  })

export class CovetPicsWidget {
  @Element() el: HTMLCovetPicsWidgetElement;

  /**
  * Shopify domain from Covet.pics API
  */
  @Prop() shop: string;

  /**
   * Gallery embed ID from Covet.pics API
   */
  @Prop() galleryEmbedId: number;

  /**
   * product id in case product handle can be translated
   */
  @Prop() shopifyProductId: number;

  /**
   * App environment: shows which API base URL has to be used
   */
  @Prop() environment: string;

  /**
   * App customSettings for testing new layouts
   */
  @Prop() customSettings: string;

  /**
   * Hide elements with CSS selector (#id \ .css-class),
   * if gallery is empty
   */
  @Prop() hideElements: string;

  /**
   * Shows whether widget is embedded to a preview page
   */
  @Prop() previewMode: boolean = false;

  /**
  * Photo request ID
  */
  @Prop() photoRequestId: number;

  @State() isLoading: boolean = true;
  @State() minNumberOfPhotos: number;
  @State() items: Array<object> = [];
  @State() appStore: AppStoreType;
  @State() fetchingError: string = null;
  private loader: GalleryLoaderService;
  private fontsLoaded: boolean = false;
  private resizeObserver: ResizeObserver;
  private sharedItemId: string;
  private filters: object = {};
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;

  /**
   * Emits when user change "Upload Widget" cover image
   */
  @Event({ composed: true }) updateUploadWidgetCover: EventEmitter;

  /**
   * emitted when parent element is resized
   */
  @Event({ composed: true }) elementResize: EventEmitter;

  @Listen("sortUpdated")
  async sortUpdatedHandler(event: CustomEvent) {
    this.loader.resetPagination();
    this.loader.sortType = event.detail; 
    const json = await this.loader.fetchItems();
    if (typeof json !== "undefined") {
      this.items = [];
      this.items = [...this.getItemsWithUpload(json.items)];
    }
  }

  @Listen("filtersUpdated")
  async filtersUpdatedHandler(event) {
    const isResetFilters = event.detail.resetFilters;
    
    if (this.appState.preview.mode) { delete this.loader.previewSettings["filters"]; } // TODO refactor this to action
    const clearFilters = () => {
      Object.keys(this.filters).forEach(f => this.filters[f] = false);
      this.loader.setSelectedFilters([]);
    };

    if (isResetFilters) {
      clearFilters();
    } else {
      const filter = event.detail.filter;
      if (!this.appState.settings.multiple_filter_enabled && !this.filters[filter]) { // toggle filter in both cases One filter and Multiple filters
        clearFilters();
      } 
      this.filters[filter] = !this.filters[filter]; // toggle filter in both cases One filter and Multiple filters
    }
    
    const filtersSelected = Object.keys(this.filters).filter(f => this.filters[f]);
    this.loader.setSelectedFilters(filtersSelected);
    this.loader.resetPagination();
    const json = await this.loader.fetchItems();
    if (typeof json !== "undefined") {
      this.items = [];
      this.items = [...this.getItemsWithUpload(json.items)];
    }
  }

  @Listen("nextPage")
  async nextPageHandler() {
    const json = await this.loader.fetchItems();
    if (typeof json !== "undefined") {
      this.items = [...this.items, ...json.items];
    }
  }

  /**
   * Update "Upload Widget" cover image
   */
  @Method()
  async updateUploadWidgetCoverPreview(newValue?: string) {
    this.updateUploadWidgetCover.emit({
      image: newValue,
    });
  }

  /**
   * Update preview settings & reload data
   */
  @Method()
  async updatePreviewSettings(newValue: object, hoverPreview: boolean, popupPreview: boolean) {
    this.loader.previewHoverMode = hoverPreview;
    this.loader.previewPopupMode = popupPreview;
    this.appStore.dispatch(setPreviewHoverMode(hoverPreview));
    this.appStore.dispatch(setPreviewPopupMode(popupPreview));
    this.fontsLoaded = false;
    this.loader.previewSettings = {
      ...this.loader.previewSettings,
      ...newValue
    };

    this.loader.resetPagination();
    this.loadData();
  }

  componentWillLoad() {
    this.initAppStore();
    this.setupLoader();
    this.loadData();
  }

  componentDidLoad() {
    this.resizeObserver = new ResizeObserver(() => {
      const isWindowHeightChanged = this.appState.size.windowHeight !== window.innerHeight;
      if (isWindowHeightChanged) {
        this.appStore.dispatch(windowHeightUpdated(window.innerHeight));
      }

      const isWidthChanged = this.appState.size.widgetWidth !== this.el.getBoundingClientRect().width;
      if (this.isWidgetWrapExist() && isWidthChanged) {
        this.setWidgetWidth();
        this.elementResize.emit();
      }
    });

    if (this.isWidgetWrapExist()) {
      this.resizeObserver.observe(this.el.parentElement);
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
    this.resizeObserver.disconnect();
  }

  private initAppStore() {
    this.appStore = configureStore();
    const widgetIdParams = getWidgetId(this.galleryEmbedId, this.shop, this.photoRequestId, this.el);
    this.appStore.dispatch(widgetIdUpdated(widgetIdParams));
    this.appStore.dispatch(setEnvironment(this.environment));
    this.appStore.dispatch(setPreviewMode(this.previewMode));
    this.appState = this.appStore.getState(); 
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState(); 
    });
    this.setWidgetWidth();
  }

  private setupLoader() {
    const uri = window.location;

    this.loader = new GalleryLoaderService(this.environment, this.previewMode);

    if (typeof this.customSettings === "string") {
      this.loader.setCustomSettings(JSON.parse(this.customSettings));
    }

    if (typeof this.galleryEmbedId === "undefined" || this.galleryEmbedId === 0) {

      if (this.shopifyProductId !== undefined) {
        this.loader.productHandle = `shopify/${this.shopifyProductId}`;
      } else if (this.el.getAttribute("data-product") !== null) {
        this.loader.productHandle = this.el.getAttribute("data-product");
      } else if (uri.pathname.indexOf("/products/") !== -1) {
        this.loader.productHandle = uri.pathname.match(/\/products\/([a-zA-Z0-9\-\_\%]+)/)[1];
      }
      
      if (this.el.hasAttribute("data-shop")) {
        this.shop = this.el.getAttribute("data-shop");
      }

      if (typeof this.shop === "undefined") {
        this.fetchingError = "The Covet.pics gallery embed has not loaded due to a missing Embed ID. Please make sure to fill in the correct Embed ID";
        this.isLoading = false;
        return;
      } else {
        this.loader.shopDomain = this.shop;
      }
    } else {
      this.loader.embedId = this.galleryEmbedId;
    }
  }

  private async loadData() {
    if (this.fetchingError !== null) return;
    this.isLoading = true;
    try {
      const json = await this.loader.fetchItems();
      //TODO test set settings after call endpoint use loader
      this.appStore.dispatch(widgetGalleryIdUpdated(this.loader.galleryId));
      this.appStore.dispatch(widgetShopIdUpdated(this.loader.shopId));
      if (this.loader.data.analytics) {
        this.appStore.dispatch(setAnalytics(this.loader.data.analytics));
      } else {
        this.appStore.dispatch(setAnalytics(false));
      }
      //TODO test set settings after call endpoint use loader END
      if (typeof json === "undefined") return false;
      this.appStore.dispatch(setSettings(json.settings));
      this.minNumberOfPhotos = json.settings.min_number_of_photos;
      this.loader.data.settings.display_type === "highlight"
        ? this.items = [...json.items]
        : this.items = [...this.getItemsWithUpload(json.items)];

      this.hideElementsIfEmpty();
      if (!this.fontsLoaded) { this.loadFont(); }
      if (this.hasEnoughPhotos && getUseAnalytics(this.appState)) {
        const category = "Covet.pics Gallery";
        const action = "Covet.pics Gallery View";
        const label = "Covet.pics Gallery View - " + this.galleryEmbedId;
        sendToGoogleAnalytics(action, category, label);
      }

      if (this.hasEnoughPhotos) {
        sendBeacon(this.appState, "gallery_view");
      }
      
      this.sharedItemId = await this.findAndLoadSharedItemId(); // TODO test ver
      this.initFilters();
      
      this.isLoading = false;
      return true;
    } catch (err) {
      if (err.message === "404") {
        this.fetchingError = "The Covet.pics gallery embed has not loaded because the embed does not exist. Please verify that the correct Embed ID is being used";
      }
      this.isLoading = false;
      return this.fetchingError;
    }
  }

  private async findAndLoadSharedItemId(): Promise<string> {
    return new Promise(async resolve => {
      if (this.loader.previewPopupMode) {
        const first = (this.items as Array<any>).find(item => item.source !== "upload_widget");
        return resolve(typeof first !== "undefined" 
          ? first.id.toString() 
          : "none");
      } 

      const itemId = findSharedItemPopup(this.loader.embedId, this.loader.galleryType);
      if (itemId === null) return resolve("none");
      const isItemLoaded = (this.items as Array<any>).findIndex(item => item.id === parseInt(itemId)) !== -1;
    
      if (isItemLoaded) return resolve(itemId.toString());

      const json = await this.loader.fetchItems();
      if (typeof json === "undefined") return resolve("none");
      this.items = [...this.items, ...json.items];
      return resolve(this.findAndLoadSharedItemId());
    });
  }

  private setWidgetWidth() {
    const width = this.isWidgetWrapExist()
      ? this.el.getBoundingClientRect().width
      : 320;
    return this.appStore.dispatch(widgetWidthUpdated(width));
  }

  private isWidgetWrapExist() {
    return (this.el.parentElement !== undefined && this.el.parentElement instanceof HTMLElement);
  }

  private get hasEnoughPhotos(): boolean {
    if (this.items.length === 0) return false;
    if (this.minNumberOfPhotos === null) return true;
    if (this.loader === undefined || this.loader.data === undefined) return false;
    if (this.loader.data.items !== undefined && this.loader.data.items.length < this.minNumberOfPhotos && this.loader.page === 1) return false;

    return true;
  }

  private initFilters() {
    const previewFilters = this.loader.previewSettings["filters"];

    this.loader.data.filters.list.forEach(f => this.filters[f] = false);

    // restore selected filters between preview reloads
    if (this.appState.preview.mode && typeof previewFilters !== "undefined") {
      previewFilters.forEach(activeFilter => this.filters[activeFilter] = true);
    }
  }

  private loadFont() {
    if (this.loader.data.settings.font_family) {
      const fontStylesheet = document.createElement("link");
      fontStylesheet.setAttribute("rel", "stylesheet");
      fontStylesheet.setAttribute("href", "https://fonts.googleapis.com/css2?family=" + this.loader.data.settings.font_family.split(" ").join("+") + ":wght@300;400;600;700&display=swap");
      document.head.appendChild(fontStylesheet);
    }
    this.fontsLoaded = true;
  }

  private setWidgetCSSVariables() {
    let cssVariables = {};
    if (this.loader.data.settings.font_family && this.loader.data.settings.font_family !== "") {
      cssVariables["--covet-internal-font-family"] = `${this.appState.settings.font_family}`;
    }
    return cssVariables;
  }

  private getItemsWithUpload(items: Array<object>) {
    if (!this.loader.data.settings.customer_photo_item_position) return items;
    const uploadIndex = this.loader.data.settings.customer_photo_item_position - 1;
    const uploadItem = { 
      source: "upload_widget", 
      imageUrl: this.appState.settings.customer_widget_cover !== null ? this.appState.settings.customer_widget_cover : undefined
    };
    items.splice(uploadIndex, 0, uploadItem);
    return items;
  }

  private renderSliderGallery() {   
    return (
      <Host style={this.setWidgetCSSVariables()} class="needsclick" /* FIXME: https://github.com/ftlabs/fastclick/issues/604 */>
        <covet-pics-gallery-slider
          items={this.items}
          shared-item-id={this.sharedItemId}
          slider-style={this.loader.data.settings.slideshow_style}
          appStore={this.appStore}
        >
        </covet-pics-gallery-slider>
      </Host>
    );
  }

  private renderGridGallery() {
    return (
      <Host style={this.setWidgetCSSVariables()} class="needsclick" /* FIXME: https://github.com/ftlabs/fastclick/issues/604 */>
        <covet-pics-gallery-grid
          items={this.items}
          rating={this.loader.data.reviews?.rating}
          rating-count={this.loader.data.reviews?.rating_count}
          product-title={this.loader.data.product_title}
          breakdown={this.loader.data.reviews?.breakdown}
          filters-enabled={this.loader.data.filters.enabled}
          shared-item-id={this.sharedItemId}
          filters={this.filters}
          show-load-more={this.appState.settings.pagination === "button" && !this.isLoading && this.loader.totalPages > this.loader.page && this.items.length > 0}
          appStore={this.appStore}>
        </covet-pics-gallery-grid>
      </Host>
    );
  }

  private renderHighlighted() {
    return (
      <Host style={this.setWidgetCSSVariables()} class="highlighted">
        <covet-pics-highlighted
          numberOfPages={this.loader.data.items.length}
          items={this.loader.data.items}
          appStore={this.appStore}
        >
        </covet-pics-highlighted>
      </Host >
    );
  }

  private hideElementsIfEmpty() {
    let selector = this.hideElements;
    if (!selector) {
      selector = this.loader.data.settings.selector_to_hide;
    }
    const items = this.loader.data.items;
    const isEmpty = (typeof items !== "undefined" && items.length === 0) || !this.hasEnoughPhotos;

    if (isEmpty && selector !== "") {
      const nodes = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
      nodes.forEach(node => node.style.display = "none");
    }
  }

  private renderUploadStandalone() {
    return (
      <covet-pics-upload standalone={true} appStore={this.appStore}></covet-pics-upload>
    );
  }

  private renderError() {
    return (
      <Host>
        <p class="error-message">{this.fetchingError}</p>
      </Host>
    );
  }

  render() {
    if (!this.isLoading && this.fetchingError !== null) return this.renderError();
    if (!this.isLoading && this.hasEnoughPhotos) {
      const displayType = this.loader.data.settings.display_type;
      if (displayType === "upload-standalone") return this.renderUploadStandalone(); // case render only upload widget
      if (displayType === "slider") return this.renderSliderGallery();
      if (displayType === "highlight") return this.renderHighlighted();
      return this.renderGridGallery();
    }
  }
}
